import React, { useEffect, useMemo, useState } from "react";
import "./WarningForNurtureGate.css";
import { checkContactExistsInCampaign, getAccountwiseCampaignList } from "../../../../../services/iqApiV2";
import { useUser } from "../../../../../context/UserContext";
import { AlertTriangle } from "react-feather";
import GeneratingLoader from "../../../genericComponents/uiElements/GeneratingLoader";

/**
 * Renders a warning if an Opportunity or Contact is already being nurtured
 * by another user's campaign.
 *
 * @param {string} clientName - The client name.
 * @param {string} accountName - The account name.
 * @param {string} [oppId] - The Opportunity ID. Required if contextType is 'opportunity'.
 * @param {string} [contactId] - The Contact ID. Required if contextType is 'contact'.
 * @param {'opportunity' | 'contact'} contextType - Specifies whether the component is gating an 'opportunity' or a 'contact'.
 * @param {React.ReactNode} [children] - The content to render if no warning is needed.
 */
function WarningForNurtureGate({
  clientName,
  accountName,
  oppId,
  contactId,
  contextType="opportunity", // New prop to specify context
  children = null,
}) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useUser();

  // Determine the ID and its type based on the contextType prop
  const relevantId = contextType === 'opportunity' ? oppId : contactId;
  const contextName = contextType === 'opportunity' ? 'Opportunity' : 'Contact';
  const relevantIdProp = contextType === 'opportunity' ? 'opportunity_id' : 'for_contactid';

  useEffect(() => {
    let isMounted = true;

    async function fetchCampaigns() {
      try {
        const resp = await getAccountwiseCampaignList(
          clientName,
          accountName,
          oppId,
          contactId // Pass both IDs to the API, let the client filter for relevance
        );

        if (!isMounted) return;

        // Filter based on the relevant ID and contextType
        const relevant = resp?.data?.filter(
          (item) => item?.[relevantIdProp] === relevantId
        );

        setCampaigns(relevant || []);
      } catch (err) {
        console.error("NurtureWarningGate error:", err);
        if (isMounted) setCampaigns([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    async function fetchCampaignsPerContact() {
      try {
        const ExistingTROdata  = await checkContactExistsInCampaign(clientName ,accountName,[contactId]);
        const checkBusy = ExistingTROdata?.[ExistingTROdata?.length -1 ]?.campaign;
        setCampaigns(checkBusy || []);
        if (!isMounted) return;

      } catch (err) {
        console.error("NurtureWarningGate error:", err);
        if (isMounted) setCampaigns([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    // Only fetch if required props are present
    if (clientName && accountName && relevantId && contextType=="opportunity") {
      fetchCampaigns();
    }else if(clientName && accountName && relevantId && contextType=="Comtact"){
      fetchCampaignsPerContact();
    } else {
        // If required props are missing, treat as not loading and no campaigns
        setLoading(false);
        setCampaigns([]);
    }

    return () => {
      isMounted = false;
    };
  }, [clientName, accountName, relevantId, contextType, relevantIdProp]); // Dependency array updated

  const { isSameUser, userList } = useMemo(() => {
    const userIds = new Set();
    const userNames = new Set();

    campaigns.forEach((camp) => {
      if (camp?.user_id) userIds.add(camp.user_id);
      if (camp?.user_name) userNames.add(camp.user_name);
    });

    const sameUser = userIds.has(user?.id);
    return { isSameUser: sameUser, userList: Array.from(userNames) };
  }, [campaigns, user?.id]);

  if (loading) return <div style={{ display: "flex", justifyContent: "center", alignItems: "center",position:"absolute",width:"100%",height:"30%" }}><GeneratingLoader /></div>;

  console.log("WarningForNurtureGate", campaigns, isSameUser, contextType);

  // Check if a warning is necessary
  const showWarning = campaigns.length > 0 && !isSameUser;

  return (
    <>
      {showWarning ? (<div style={{ display: "flex", justifyContent: "center", alignItems: "center",position:"absolute",width:"100%",height:"40%" }}>
        <div className="nurture-warning-container">
          <div className="nurture-warning-card">
            <div className="nurture-warning-inner">
              <div className="nurture-warning-symbol">
                <AlertTriangle size={16} />
              </div>
              <span className="nurture-warning-message">
                This <strong>{contextName}</strong> is already being nurtured {" "}
                { userList?.length>0 && " by " + userList.join(", ")}
              </span>
            </div>
          </div>
        </div></div>
        
      ) : (
        children
      )}
    </>
  );
}

export default WarningForNurtureGate;